import { useEffect, useState, useMemo } from 'react';
import { Typography, Box, Paper, Stack, Alert, CircularProgress } from '@mui/material';
import {
  GridContainer,
  Button,
  GridItem,
  TextField,
  Select,
  Datepicker,
  Breadcrumbs,
  Autocomplete,
} from '../../../components/shared';
import { FormikProvider, useFormik } from 'formik';
import { useUpdateModemByIdMutation, useGetModemDetailsByIdQuery } from '../../../services/Modems/ModemsService';
import { useLazyGetAssetResyncStatusQuery } from '../../../services/Machines/MachinesService';
import useStyles from './style';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { routeConstants } from '../../../constants/routeConstants';
import { ModemType } from '../ModemDetails/data';
import BREADCRUMB_NAMES from '../../../constants/breadcrumbNames';
import { PerformantDecimalField } from '../../../components/shared/PerformantDecimalField/PerformantDecimalField';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import ConfirmationModal from '../../../components/ConfirmationModel';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { getDateFormat, dateFormatForApi } from '../../../utils/common-methods';
import { useAssetSearch } from '../../../hooks/useAssetSearch';

dayjs.extend(utc);

const EditModem = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();
  const location = useLocation();

  const {
    MODEM: { MODEM, MODEM_DETAIL, EDIT_MODEM },
  } = BREADCRUMB_NAMES;

  const versionFromState = location.state?.version || null;
  const type = location?.state?.type || null;

  const { isSuccess, currentData: currentModemDetails } = useGetModemDetailsByIdQuery({
    id: Number(id),
    version: versionFromState,
  });

  const updatedById = useSelector((state) => state.user?.userDetails?.id);

  const {
    assetOptions: assetOptionsFromHook,
    isAssetError,
    handleInputChange: handleAssetInputChange,
    getHelperText,
  } = useAssetSearch();

  const currentAssetId = currentModemDetails?.data?.assetId;
  const currentAssetTitle = currentModemDetails?.data?.asset?.title;

  const assetIds =
    currentAssetId && !assetOptionsFromHook.some((item) => item.value === currentAssetId)
      ? [{ text: `${currentAssetId} (${currentAssetTitle || ''})`, value: currentAssetId }, ...assetOptionsFromHook]
      : assetOptionsFromHook;

  const [updateModemApi] = useUpdateModemByIdMutation();
  const [getAssetResyncStatus] = useLazyGetAssetResyncStatusQuery();

  const isResyncEnabled = process.env.REACT_APP_ENABLE_FEED_RESYNC === 'true';

  // Check if resync is in progress for this modem
  const isResyncInProgress = useMemo(() => {
    if (!isResyncEnabled) return false;
    const resync = currentModemDetails?.data?.resync;
    if (!resync) return false;
    const items = Array.isArray(resync) ? resync : Object.values(resync).flat();
    return items.some((item) => item && item.status !== 'completed');
  }, [isResyncEnabled, currentModemDetails?.data?.resync]);

  // Modal state for override confirmation
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [apiError, setApiError] = useState('');
  const [resyncWarning, setResyncWarning] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Function to format error message: replace 'Future' with 'Present'
  const formatErrorMessage = (message) => {
    if (!message) return '';
    return message.replace(/Future/g, 'Present');
  };

  const modemUpdateSchema = Yup.object().shape({
    type: Yup.string().required('Type is required'),
    installedDate: Yup.string(),
    price: Yup.number().required('Price is required'),
    loginId: Yup.string(),
    password: Yup.string(),
    MACAddress: Yup.string(),
    assetId: Yup.string(),
    inEffectFrom: Yup.string().required('In Effect From is required'),
  });

  const formik = useFormik({
    initialValues: {
      type: '',
      installedDate: '',
      price: 0,
      loginId: '',
      password: '',
      serialNumber: '',
      MACAddress: '',
      assetId: '',
      inEffectFrom: '',
      inEffectTo: '',
    },
    validationSchema: modemUpdateSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (data) => {
      setApiError('');
      setResyncWarning('');
      setIsLoading(true);

      // Check if the assigned asset has resync in progress
      if (isResyncEnabled && data.assetId) {
        try {
          const resyncResult = await getAssetResyncStatus(data.assetId).unwrap();
          if (resyncResult?.data?.resync === true) {
            setResyncWarning(
              'A resync is currently in progress for this asset. Please try again after resync is complete.',
            );
            setIsLoading(false);
            return;
          }
        } catch {
          // If resync check fails, proceed with submission
        }
      }

      const payload = {
        id: id,
        type: data.type,
        installedDate: data.installedDate ? dateFormatForApi(data.installedDate) : '',
        price: data.price || 0,
        loginId: data?.loginId || '',
        password: data?.password || '',
        MACAddress: data.MACAddress || null,
        assetId: data.assetId || null,
        inEffectFrom: data.inEffectFrom ? dateFormatForApi(data.inEffectFrom) : null,
        inEffectTo: data.inEffectTo ? dateFormatForApi(data.inEffectTo) : null,
        updatedBy: updatedById,
        version: currentModemDetails?.data?.version || null,
        override: false,
      };
      await updateModemApi(payload)
        .unwrap()
        .then(() => {
          onSaveHandler();
        })
        .catch((error) => {
          setIsLoading(false);
          if (error.status === 409) {
            setApiError(error.data?.message || 'A conflict occurred while saving the record.');
            setShowOverrideModal(true);
            setPendingFormData(data);
          }
        });
    },
  });

  const { values, errors, touched, getFieldProps, setFieldValue, setValues } = formik;

  useEffect(() => {
    if (isSuccess && currentModemDetails?.data) {
      setValues({
        type: currentModemDetails?.data?.modem?.type || '',
        installedDate: currentModemDetails?.data?.modem?.installedDate || '',
        price: currentModemDetails?.data?.price || 0,
        loginId: currentModemDetails?.data?.loginId || '',
        password: currentModemDetails?.data?.password || '',
        serialNumber: currentModemDetails?.data?.modem?.serialNumber || '',
        MACAddress: currentModemDetails?.data?.modem?.MACAddress || '',
        assetId: currentModemDetails?.data?.assetId || '',
        inEffectFrom: currentModemDetails?.data?.inEffectFrom || '',
        inEffectTo: currentModemDetails?.data?.inEffectTo || '',
      });
    }
  }, [isSuccess, currentModemDetails, setValues]);

  const breadcrumbData = [
    { text: MODEM, redirection: `${routeConstants.MODEMS_ROUTE}` },
    { text: MODEM_DETAIL, redirection: `${routeConstants.MODEMS_ROUTE}/${id}` },
    { text: EDIT_MODEM },
  ];

  const onCancelHandler = () => {
    navigate(`/${routeConstants.MODEMS_ROUTE}/${id}`, { redirect: true });
  };

  const onSaveHandler = () => {
    navigate(`/${routeConstants.MODEMS_ROUTE}/${id}`, { redirect: true });
  };

  const handleAssetChange = (e, value) => {
    const newAssetId = value ? value.value : '';
    setFieldValue('assetId', newAssetId);
  };

  // Handler for confirming override in modal
  const handleOverrideConfirm = async () => {
    setShowOverrideModal(false);
    setIsLoading(true);
    if (!pendingFormData) return;
    const payload = {
      id: id,
      type: pendingFormData.type,
      installedDate: pendingFormData.installedDate ? dateFormatForApi(pendingFormData.installedDate) : '',
      price: pendingFormData.price || 0,
      loginId: pendingFormData?.loginId || '',
      password: pendingFormData?.password || '',
      MACAddress: pendingFormData.MACAddress || null,
      assetId: pendingFormData.assetId || null,
      inEffectFrom: pendingFormData.inEffectFrom ? dateFormatForApi(pendingFormData.inEffectFrom) : null,
      inEffectTo: pendingFormData.inEffectTo ? dateFormatForApi(pendingFormData.inEffectTo) : null,
      updatedBy: updatedById,
      version: currentModemDetails?.data?.version || null,
      override: true,
    };
    const response = await updateModemApi(payload);
    if (response.data) {
      onSaveHandler();
    } else {
      setIsLoading(false);
    }
    setPendingFormData(null);
    setApiError('');
  };

  // Handler for cancelling override in modal
  const handleOverrideCancel = () => {
    setShowOverrideModal(false);
    setPendingFormData(null);
    setApiError('');
  };

  return (
    <Box>
      <Box className={classes.breadcrumbContainer}>
        <Breadcrumbs icon={'/'} variant="smallText" underline="hover" data={breadcrumbData} />
      </Box>
      <Box component={Paper} className={classes.formContent} sx={{ position: 'relative' }}>
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <Typography variant="h2" pt={3} pl={3}>
          Modem Details
        </Typography>
        {isResyncInProgress && (
          <Box px={3} pt={2}>
            <Alert severity="warning">
              A resync is currently in progress for this modem. Please try again after resync is complete.
            </Alert>
          </Box>
        )}
        {resyncWarning && (
          <Box px={3} pt={2}>
            <Alert severity="warning" onClose={() => setResyncWarning('')}>
              {resyncWarning}
            </Alert>
          </Box>
        )}
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <GridContainer spacing={{ xs: 1, sm: 2, md: 3 }} alignItems="center">
              <GridItem xs={12} md={6}>
                <Select
                  inputLabel="Type"
                  data-testid="editModemForm"
                  options={ModemType}
                  {...getFieldProps('type')}
                  error={Boolean(touched.type && errors.type)}
                  helperText={touched.type && errors.type}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <TextField
                  label="MAC Address"
                  InputProps={{
                    'data-testid': 'editModemForm',
                  }}
                  {...getFieldProps('MACAddress')}
                  error={Boolean(touched.MACAddress && errors.MACAddress)}
                  helperText={touched.MACAddress && errors.MACAddress}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <PerformantDecimalField
                  useFormik={true}
                  priceIcon={true}
                  label="Price"
                  {...getFieldProps('price')}
                  error={Boolean(touched.price && errors.price)}
                  helperText={touched.price && errors.price}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <TextField
                  label="Login ID"
                  InputProps={{
                    'data-testid': 'editModemForm',
                  }}
                  {...getFieldProps('loginId')}
                  error={Boolean(touched.loginId && errors.loginId)}
                  helperText={touched.loginId && errors.loginId}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <TextField
                  label="Password"
                  InputProps={{
                    'data-testid': 'editModemForm',
                  }}
                  {...getFieldProps('password')}
                  error={Boolean(touched.password && errors.password)}
                  helperText={touched.password && errors.password}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <TextField
                  label="Serial Number"
                  disabled
                  InputProps={{
                    'data-testid': 'editModemForm',
                  }}
                  {...getFieldProps('serialNumber')}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <Datepicker
                  label="Installed Date"
                  value={values.installedDate ? getDateFormat(values.installedDate) : null}
                  data-testid="editModemForm"
                  inputFormat="MM/DD/YYYY"
                  onChange={(newValue) => {
                    if (newValue) {
                      // Store as YYYY-MM-DD format
                      setFieldValue('installedDate', dayjs(newValue).format('YYYY-MM-DD'));
                    } else {
                      setFieldValue('installedDate', '');
                    }
                  }}
                  onBlur={() => formik.setFieldTouched('installedDate')}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <Autocomplete
                  label="Asset ID"
                  data-testid="editModemAssetId"
                  value={assetIds.find((option) => option.value === values.assetId) || null}
                  options={assetIds}
                  getOptionLabel={(option) => option.text || ''}
                  onChange={handleAssetChange}
                  onInputChange={(event, newValue, reason) => {
                    if (reason === 'input') {
                      handleAssetInputChange(newValue);
                    }
                  }}
                  onBlur={() => formik.setFieldTouched('assetId')}
                  error={isAssetError}
                  helperText={getHelperText(values.assetId)}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <Datepicker
                  label="In Effect From"
                  value={values.inEffectFrom ? dayjs.utc(values.inEffectFrom) : null}
                  inputFormat="MM/DD/YYYY"
                  minDate={type === 'fromDetails' ? dayjs.utc(currentModemDetails?.data?.inEffectFrom) : undefined}
                  maxDate={dayjs()}
                  onChange={(newValue) => {
                    if (newValue && newValue.isValid()) {
                      setFieldValue('inEffectFrom', newValue.format('YYYY-MM-DD'));
                    } else {
                      setFieldValue('inEffectFrom', '');
                    }
                  }}
                  onBlur={() => formik.setFieldTouched('inEffectFrom')}
                  error={Boolean(touched.inEffectFrom && errors.inEffectFrom)}
                  helperText={touched.inEffectFrom && errors.inEffectFrom}
                  disabled={(type !== 'add' || type === 'fromDetails') && type === 'edit'}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <Datepicker
                  label="In Effect To"
                  value={values.inEffectTo ? dayjs.utc(values.inEffectTo) : null}
                  inputFormat="MM/DD/YYYY"
                  minDate={values.inEffectFrom ? dayjs.utc(values.inEffectFrom) : undefined}
                  maxDate={dayjs().subtract(1, 'day')}
                  onChange={(newValue) => {
                    if (newValue && newValue.isValid()) {
                      setFieldValue('inEffectTo', newValue.format('YYYY-MM-DD'));
                    } else {
                      setFieldValue('inEffectTo', '');
                    }
                  }}
                  onBlur={() => formik.setFieldTouched('inEffectTo')}
                  error={Boolean(touched.inEffectTo && errors.inEffectTo)}
                  helperText={touched.inEffectTo && errors.inEffectTo}
                  disabled={type === 'edit' || type === 'fromDetails'}
                />
              </GridItem>
            </GridContainer>
            <GridContainer justifyContent="space-around" pl={4}>
              <GridItem xs={12} md={12}>
                <Stack direction={'row'} className={classes.btn}>
                  <Stack direction={'row'}>
                    <Button type="Submit" variant="contained" disabled={isResyncInProgress || isLoading}>
                      <Typography variant="button">UPDATE</Typography>
                    </Button>
                    <Button onClick={onCancelHandler} disabled={isLoading}>
                      <Typography variant="button">CANCEL</Typography>
                    </Button>
                  </Stack>
                </Stack>
              </GridItem>
            </GridContainer>
          </form>
        </FormikProvider>
      </Box>
      {/* Override Confirmation Modal */}
      <ConfirmationModal
        isOpen={showOverrideModal}
        title="Update Confirmation"
        msg={
          formatErrorMessage(apiError) ||
          'There is a conflict with the modem data. Do you want to override and continue?'
        }
        buttons={[
          { text: 'Confirm', value: 'confirm' },
          { text: 'Cancel', value: 'cancel' },
        ]}
        onClick={(val) => {
          if (val === 'confirm') {
            handleOverrideConfirm();
          } else {
            handleOverrideCancel();
          }
        }}
      />
    </Box>
  );
};

export default EditModem;
